// 비즈니스 예절 교육 콘텐츠 데이터

export interface EtiquetteTip {
  id: string;
  category: string;
  categoryIcon: string;
  categoryColor: string;
  title: string;
  summary: string;
  detail: string;
  doList: string[];
  dontList: string[];
  situation?: string; // 어떤 상황에서 보여줄지
  level: "basic" | "intermediate" | "advanced";
}

export interface EtiquetteCategory {
  key: string;
  label: string;
  icon: string;
  color: string;
  description: string;
}

export const ETIQUETTE_CATEGORIES: EtiquetteCategory[] = [
  { key: "email", label: "이메일 예절", icon: "ri-mail-line", color: "#4B6CF7", description: "비즈니스 이메일의 형식과 톤을 익혀 신뢰감을 줍니다" },
  { key: "meeting", label: "회의 예절", icon: "ri-team-line", color: "#7C6FF7", description: "회의에서 존재감을 드러내고 효율적으로 기여하는 방법" },
  { key: "report", label: "보고 예절", icon: "ri-file-text-line", color: "#059669", description: "상사에게 올바르게 보고하고 피드백을 받는 스킬" },
  { key: "communication", label: "소통 예절", icon: "ri-chat-3-line", color: "#D97706", description: "동료·상사·외부 파트너와의 올바른 커뮤니케이션" },
  { key: "document", label: "문서 예절", icon: "ri-file-paper-2-line", color: "#EA580C", description: "비즈니스 문서 작성의 기본과 공유 에티켓" },
  { key: "relationship", label: "직장 관계 예절", icon: "ri-user-heart-line", color: "#E8375F", description: "선배·동료·후배와 건강한 관계를 만드는 방법" },
  { key: "time", label: "시간 예절", icon: "ri-time-line", color: "#0D9488", description: "약속 시간, 마감, 응답 속도에 관한 직장인 상식" },
  { key: "digital", label: "메신저/전화 예절", icon: "ri-smartphone-line", color: "#6B9FF7", description: "카카오톡·슬랙·전화에서의 올바른 디지털 소통법" },
];

export const ETIQUETTE_TIPS: EtiquetteTip[] = [
  // ─── 이메일 ───────────────────────────────────────────────────
  {
    id: "email_01",
    category: "email",
    categoryIcon: "ri-mail-line",
    categoryColor: "#4B6CF7",
    title: "이메일 제목은 핵심을 30자 이내로",
    summary: "제목만 봐도 무슨 메일인지 알 수 있게 써야 합니다.",
    detail: "바쁜 직장인은 수십 통의 이메일을 받습니다. 제목에 '안녕하세요'나 '문의드립니다'만 써놓으면 우선순위에서 밀립니다. [요청], [공유], [확인 요청], [긴급] 같은 태그를 앞에 붙이면 더 명확합니다.",
    doList: ["[확인 요청] 루나&스타즈 계약서 v2 검토 부탁드립니다", "[공유] 3월 마케팅 성과 보고서 첨부", "[긴급] 오늘 오전까지 예산안 최종 승인 필요"],
    dontList: ["안녕하세요", "문의드립니다", "부탁드립니다", "메일드립니다"],
    situation: "compose",
    level: "basic",
  },
  {
    id: "email_02",
    category: "email",
    categoryIcon: "ri-mail-line",
    categoryColor: "#4B6CF7",
    title: "인사말 → 본론 → 마무리 3단 구조",
    summary: "모든 비즈니스 이메일은 이 구조를 따릅니다.",
    detail: "1단 인사말: 상대방과의 관계·상황에 맞는 짧은 인사. 2단 본론: 메일의 목적을 첫 문장에 명확히 밝히고, 필요한 내용을 간결하게 전달. 3단 마무리: 기대 행동(회신 요청, 검토 요청 등)을 명시하고 정중하게 마무리.",
    doList: [
      "안녕하세요, 마케팅 담당 ○○○입니다.",
      "다름이 아니라 ～에 대해 말씀드리고자 연락드렸습니다.",
      "바쁘신 중에도 검토해 주시면 감사하겠습니다.",
    ],
    dontList: [
      "서론 없이 바로 요청사항만 쓰기",
      "한 이메일에 여러 가지 요청 섞기",
      "감사 인사 없이 마무리하기",
    ],
    situation: "compose",
    level: "basic",
  },
  {
    id: "email_03",
    category: "email",
    categoryIcon: "ri-mail-line",
    categoryColor: "#4B6CF7",
    title: "24시간 이내 회신이 원칙",
    summary: "당장 답을 못해도 '수신 확인 + 처리 예정 시간'은 알려야 합니다.",
    detail: "바쁜 업무 중 즉시 답변이 어렵더라도, '확인했으며 ○일까지 답변드리겠습니다'라는 회신만으로도 상대방은 안심합니다. 침묵은 무시로 해석될 수 있습니다.",
    doList: ["확인했습니다. 내일 오전 중으로 검토 후 회신드리겠습니다.", "수신 확인했습니다. 관련 팀과 협의 후 이번 주 내 답변드리겠습니다."],
    dontList: ["며칠째 무응답", "나중에 답장할게를 잊어버리기", "바빠서 못 봤다고 변명하기"],
    situation: "reply",
    level: "basic",
  },
  {
    id: "email_04",
    category: "email",
    categoryIcon: "ri-mail-line",
    categoryColor: "#4B6CF7",
    title: "CC(참조)는 꼭 필요한 사람에게만",
    summary: "CC를 남발하면 스팸이 되고, 빠뜨리면 오해가 생깁니다.",
    detail: "CC는 직접 당사자가 아니지만 내용을 알아야 할 사람에게 씁니다. 상사를 CC에 넣는 것은 '보고 드렸다'는 의미도 됩니다. BCC는 수신자들끼리 서로 모르는 사람에게 대량 발송할 때 사용합니다.",
    doList: ["계약 관련 메일: 법무·재무 팀장 CC", "외부 파트너 협의: 담당 상사 CC", "대량 공지: 모든 수신자 BCC"],
    dontList: ["대표 포함 전 직원 CC", "관련 없는 사람 CC로 압박", "CC 없이 업무 결정 메일 발송"],
    situation: "compose",
    level: "intermediate",
  },
  {
    id: "email_05",
    category: "email",
    categoryIcon: "ri-mail-line",
    categoryColor: "#4B6CF7",
    title: "파일 첨부 시 파일명 규칙",
    summary: "파일명만 봐도 내용을 알 수 있게 이름 짓는 것이 기본입니다.",
    detail: "파일을 받은 사람 입장에서 '파일명1.pdf', 'final_final_수정본2.xlsx'는 혼란을 줍니다. 날짜·버전을 포함한 명확한 이름을 쓰면 신뢰도가 높아집니다.",
    doList: ["루나스타즈_전속계약서_v2_20260325.pdf", "마케팅예산안_2026Q1_초안.xlsx", "DarkNova_MV기획서_최종.pptx"],
    dontList: ["파일.pdf", "final_final.xlsx", "수정본3.docx", "untitled.pptx"],
    situation: "compose",
    level: "basic",
  },

  // ─── 회의 ───────────────────────────────────────────────────
  {
    id: "meeting_01",
    category: "meeting",
    categoryIcon: "ri-team-line",
    categoryColor: "#7C6FF7",
    title: "회의는 5분 전 도착이 기본",
    summary: "정시 도착은 기본, 5분 전 도착이 예의입니다.",
    detail: "회의 시간에 늦으면 참석자 전원의 시간을 낭비하게 됩니다. 특히 외부 파트너와의 회의에서 지각은 신뢰도 하락으로 이어집니다. 불가피하게 늦을 경우 반드시 사전에 연락하세요.",
    doList: ["회의 5분 전 도착 + 자료 준비 완료", "불가피한 지각 시 사전 문자/메신저 알림", "시작 전 자신의 발언 순서·내용 미리 정리"],
    dontList: ["정시에 허둥지둥 도착", "회의 중 뒤늦게 들어오며 소란", "지각 후 사과 없이 자리 착석"],
    level: "basic",
  },
  {
    id: "meeting_02",
    category: "meeting",
    categoryIcon: "ri-team-line",
    categoryColor: "#7C6FF7",
    title: "발언할 때는 결론부터, 이유는 나중에",
    summary: "'PREP 법칙'으로 짧고 명확하게 말합니다.",
    detail: "P(Point): 결론 먼저 → R(Reason): 이유 → E(Example): 예시나 데이터 → P(Point): 결론 재강조. 직장에서는 상대방의 시간을 절약하는 사람이 유능한 사람입니다. 길게 설명하다 결론에 도달하는 방식은 피하세요.",
    doList: ["저는 A안이 적합하다고 생각합니다. 이유는 ○○이고, 실제로 ○○사례에서 효과가 있었습니다.", "결론: X가 필요합니다 → 근거: ～때문입니다"],
    dontList: ["한참 배경 설명 후 '그래서 뭐냐면...'", "결론 없이 현황 나열만", "~것 같아요, ~아닐까요 등 모호한 표현"],
    level: "basic",
  },
  {
    id: "meeting_03",
    category: "meeting",
    categoryIcon: "ri-team-line",
    categoryColor: "#7C6FF7",
    title: "회의 중 핸드폰은 뒤집어 두거나 가방에",
    summary: "핸드폰을 보는 것은 상대방에게 무례한 행동입니다.",
    detail: "긴급 상황이 예상된다면 회의 시작 전에 '중요 연락이 올 수 있어 잠깐 확인할 수 있다'고 미리 양해를 구하세요. 아무런 말 없이 회의 중 핸드폰을 보는 것은 매우 실례입니다.",
    doList: ["핸드폰 진동으로 전환 후 가방에", "긴급 상황 예상 시 사전 양해 구하기", "메모가 필요하면 노트나 노트북 사용"],
    dontList: ["테이블 위에 핸드폰 올려두고 수시로 확인", "회의 중 SNS 확인", "무음이라도 화면이 켜지면 시선 빼앗김"],
    level: "basic",
  },
  {
    id: "meeting_04",
    category: "meeting",
    categoryIcon: "ri-team-line",
    categoryColor: "#7C6FF7",
    title: "반대 의견은 '그런데'가 아닌 '그 말씀에 더해서'",
    summary: "의견 충돌이 아닌 토론으로 만드는 대화 기술입니다.",
    detail: "반대 의견을 직접 부정하면 방어적이 되어 회의가 감정 싸움으로 변합니다. '맞는 부분도 있는데, 한 가지 우려되는 점은...'이나 '그 방향에 더해서 이런 리스크도 검토해야 할 것 같습니다'처럼 부드럽게 전환하세요.",
    doList: ["그 아이디어 좋습니다. 추가로 ○○ 부분도 고려하면 어떨까요?", "말씀하신 방향으로 가되, ○○ 리스크를 함께 검토하면 좋겠습니다."],
    dontList: ["그건 틀렸어요", "그게 왜요?", "저는 동의 못 합니다만"],
    level: "intermediate",
  },

  // ─── 보고 ───────────────────────────────────────────────────
  {
    id: "report_01",
    category: "report",
    categoryIcon: "ri-file-text-line",
    categoryColor: "#059669",
    title: "보고는 '나쁜 소식'을 더 빨리 해야 합니다",
    summary: "좋은 소식은 늦게 말해도 되지만, 나쁜 소식은 빠를수록 좋습니다.",
    detail: "문제가 생겼을 때 상사에게 빨리 보고하면 함께 해결책을 찾을 시간이 생깁니다. 늦게 알리면 '왜 진작 말 안 했어?'라는 신뢰 문제로 이어집니다. 완벽한 해결책이 없어도 현황 보고는 해야 합니다.",
    doList: ["문제 파악 즉시: '○○ 이슈가 발생했고, 현재 이렇게 대응 중입니다'", "해결책 없어도 상황 공유는 먼저"],
    dontList: ["혼자 해결하려다 더 커진 후 보고", "상사가 먼저 알게 되는 상황", "문제를 숨기거나 축소 보고"],
    level: "basic",
  },
  {
    id: "report_02",
    category: "report",
    categoryIcon: "ri-file-text-line",
    categoryColor: "#059669",
    title: "결재 서류에는 항상 '판단 근거'가 있어야 합니다",
    summary: "상사가 YES/NO를 결정할 수 있는 정보를 담아야 합니다.",
    detail: "결재를 올릴 때는 단순한 요청만이 아니라 ①왜 필요한지 ②어떤 방법으로 할지 ③비용/리스크는 어느 정도인지 ④담당자 의견/추천안은 무엇인지를 포함해야 합니다. 상사가 추가 질문 없이 결정할 수 있어야 합니다.",
    doList: ["배경 → 목적 → 방법 → 예산 → 기대효과 → 담당자 의견 순서로 작성", "비교 대안 2~3가지 제시 후 추천안 명시"],
    dontList: ["'○○ 건으로 결재 부탁드립니다'만 쓰기", "금액은 쓰고 이유는 없는 기안서", "상사가 계속 질문해야 하는 불완전한 서류"],
    situation: "approval",
    level: "intermediate",
  },
  {
    id: "report_03",
    category: "report",
    categoryIcon: "ri-file-text-line",
    categoryColor: "#059669",
    title: "업무 지시 받을 때 반드시 재확인해야 할 3가지",
    summary: "마감일, 결과물 형식, 우선순위 — 이 세 가지는 꼭 확인하세요.",
    detail: "지시를 받으면서 바로 '네'라고 해놓고 나중에 다르게 이해했다는 것을 알게 되면 곤란합니다. 지시를 받은 직후 간단히 요약해서 확인하는 것이 실수를 줄이는 가장 좋은 방법입니다.",
    doList: [
      "지시를 받고: '그럼 ○일까지 ○형식으로 ○○팀과 공유하면 되는 건가요?'",
      "우선순위 불명확할 때: '현재 A 업무도 진행 중인데, 어느 것을 먼저 할까요?'",
    ],
    dontList: ["이해 안 된 채로 '네'", "나중에 다르게 해놓고 '그런 줄 알았어요'", "마감일 재확인 않고 임의로 추정"],
    level: "basic",
  },

  // ─── 소통 ───────────────────────────────────────────────────
  {
    id: "comm_01",
    category: "communication",
    categoryIcon: "ri-chat-3-line",
    categoryColor: "#D97706",
    title: "상사 호칭은 직책명 + '님' 이 기본",
    summary: "'김 팀장님', '이 대표님'처럼 직책 뒤에 '님'을 붙입니다.",
    detail: "입사 초기에는 공식 호칭을 쓰는 것이 안전합니다. 나이가 비슷해도 업무 관계에서는 직책을 따릅니다. '형', '오빠', '언니' 같은 호칭은 친해지더라도 공식 석상에서는 사용을 자제하세요.",
    doList: ["김태현 팀장님", "이지현 대표님", "(회의에서) 마케팅 담당자님"],
    dontList: ["야", "형/언니/오빠 (공식 자리에서)", "이름 + 씨 (오해 소지)"],
    level: "basic",
  },
  {
    id: "comm_02",
    category: "communication",
    categoryIcon: "ri-chat-3-line",
    categoryColor: "#D97706",
    title: "부탁할 때는 이유를 함께",
    summary: "'이유 있는 부탁'이 더 잘 받아들여집니다.",
    detail: "심리학 연구에 따르면 이유를 함께 말할 때 요청 수락률이 크게 높아집니다. '바쁘신 줄 알지만 ○○ 때문에 오늘 안으로 꼭 필요해서요'처럼 맥락을 설명하면 상대방이 우선순위를 결정하는 데 도움이 됩니다.",
    doList: ["클라이언트 미팅이 내일 오전이라 오늘까지 계약서 확인이 필요합니다.", "이번 주 금요일이 제출 마감이라 검토 부탁드립니다."],
    dontList: ["빨리 해주세요 (이유 없음)", "오늘까지요 (맥락 없음)", "그냥 확인 좀 해주세요"],
    level: "basic",
  },
  {
    id: "comm_03",
    category: "communication",
    categoryIcon: "ri-chat-3-line",
    categoryColor: "#D97706",
    title: "외부 파트너에게는 더 격식 있는 표현을",
    summary: "내부 소통보다 한 단계 더 공손하게 말합니다.",
    detail: "내부 팀원에게는 간결하게 말해도 되지만, 외부 파트너(유통사, 광고주, 투자자)에게는 더 격식 있는 표현을 씁니다. '~해주세요'보다 '~부탁드릴 수 있을까요?', '~주시면 감사하겠습니다'처럼.",
    doList: ["검토 부탁드릴 수 있을까요?", "말씀해 주시면 감사하겠습니다.", "회신 주시면 빠르게 처리하겠습니다."],
    dontList: ["빨리 해주세요", "왜 아직 안 됐나요?", "그거 언제 됩니까?"],
    level: "intermediate",
  },

  // ─── 문서 ───────────────────────────────────────────────────
  {
    id: "doc_01",
    category: "document",
    categoryIcon: "ri-file-paper-2-line",
    categoryColor: "#EA580C",
    title: "문서 공유 전 오탈자 검토는 필수",
    summary: "오탈자는 내용보다 더 큰 신뢰 손상을 만듭니다.",
    detail: "아무리 좋은 내용이라도 오탈자가 있으면 '대충 만든 문서'라는 인상을 줍니다. 발송 전 최소 1회 소리 내어 읽어보거나, 하루 지난 후 다시 보는 것도 좋습니다. 공식 계약서나 외부 발송 문서는 반드시 제3자 교정을 받으세요.",
    doList: ["발송 전 큰 소리로 읽기", "맞춤법 검사기 활용 (맞춤법 검사기 네이버/부산대)", "중요 문서는 동료에게 교정 요청"],
    dontList: ["작성 직후 바로 발송", "'대충 맞겠지' 생각으로 건너뛰기", "계약서에 오탈자 방치"],
    level: "basic",
  },
  {
    id: "doc_02",
    category: "document",
    categoryIcon: "ri-file-paper-2-line",
    categoryColor: "#EA580C",
    title: "결재 기안서는 '액션 아이템'이 명확해야",
    summary: "결재자가 '무엇을 결정해야 하는지' 한 눈에 보여야 합니다.",
    detail: "기안서 첫 문단에 '본 기안서는 ○○를 결재하기 위해 작성되었습니다'처럼 목적을 명확히 쓰세요. 결재자는 기안서 전체를 꼼꼼히 읽을 시간이 없는 경우가 많습니다. 요약본(Executive Summary) 형태로 한 문단을 먼저 작성하는 것이 좋습니다.",
    doList: ["첫 줄에 결재 목적 명시", "금액·일정·책임자 등 핵심 정보 표로 정리", "담당자 의견·추천안 명시"],
    dontList: ["배경만 길게 쓰고 결재 목적 불분명", "금액 없는 예산 기안서", "담당자 의견 없이 나열만"],
    situation: "approval",
    level: "intermediate",
  },

  // ─── 직장 관계 ────────────────────────────────────────────────
  {
    id: "rel_01",
    category: "relationship",
    categoryIcon: "ri-user-heart-line",
    categoryColor: "#E8375F",
    title: "도움을 받았다면 반드시 피드백을",
    summary: "'잘 됐어요' 한 마디가 협업 문화를 만듭니다.",
    detail: "동료에게 검토나 도움을 요청하고 나서 결과를 알려주지 않으면, 상대방은 '내 도움이 의미가 있었나?'라고 생각합니다. 작은 피드백이 신뢰와 협업 문화를 만듭니다.",
    doList: ["덕분에 계약 잘 마무리됐습니다. 감사해요!", "검토해 주신 덕분에 수정 포인트를 잡았습니다."],
    dontList: ["도움받고 결과 통보 없음", "잘 됐는지 안 됐는지 아무 말 없음"],
    level: "basic",
  },
  {
    id: "rel_02",
    category: "relationship",
    categoryIcon: "ri-user-heart-line",
    categoryColor: "#E8375F",
    title: "험담은 반드시 본인에게 돌아옵니다",
    summary: "사무실에서 누군가에 대한 험담은 언제나 그 사람에게 전달됩니다.",
    detail: "소규모 팀일수록 정보는 빠르게 공유됩니다. 동료나 상사에 대한 불만이 있다면 직접 소통하거나 상위 채널(HR, 팀장)을 활용하세요. 뒷담화는 본인의 평판만 떨어뜨립니다.",
    doList: ["문제가 있으면 당사자에게 직접 이야기", "불만은 건설적인 피드백으로 전환", "상급자 채널을 통한 공식적인 소통"],
    dontList: ["삼삼오오 모여서 특정인 험담", "카카오톡 단톡방에서 불평", "신입 때부터 '편 가르기'"],
    level: "basic",
  },
  {
    id: "rel_03",
    category: "relationship",
    categoryIcon: "ri-user-heart-line",
    categoryColor: "#E8375F",
    title: "실수했을 때는 즉시 인정 + 해결책",
    summary: "실수 자체보다 실수 후 대처가 평판을 결정합니다.",
    detail: "누구나 실수합니다. 중요한 것은 실수를 빠르게 인정하고, 변명보다 해결책을 먼저 제시하는 것입니다. '제 실수입니다. 이렇게 해결하겠습니다'가 '그건 원래 이래서...'보다 훨씬 좋은 인상을 남깁니다.",
    doList: ["제 실수입니다. ○○ 방법으로 즉시 수정하겠습니다.", "오늘 내로 ○○하여 보완하겠습니다."],
    dontList: ["남 탓, 시스템 탓, 환경 탓", "변명 먼저, 사과 나중", "실수를 숨기거나 모른 척"],
    level: "basic",
  },

  // ─── 시간 ───────────────────────────────────────────────────
  {
    id: "time_01",
    category: "time",
    categoryIcon: "ri-time-line",
    categoryColor: "#0D9488",
    title: "마감은 '제출 가능한 날'이 아니라 '상대방이 필요한 날'",
    summary: "마감일 = 상대방이 필요한 날 - 최소 하루 전",
    detail: "마감일 당일 밤 11시에 파일을 보내는 것은 '마감을 지킨 것'이 아닙니다. 상대방이 검토할 시간을 고려하면 하루 이상 여유를 두어야 합니다. 특히 외부 파트너나 상사에게는 마감보다 빠르게 제출하는 습관이 중요합니다.",
    doList: ["마감 최소 하루 전 제출", "지연 예상 시 마감 전날 사전 공지", "수정 요청 가능성 고려해 여유 있게 제출"],
    dontList: ["마감 당일 밤에 전송", "지연 사실을 마감 당일에 통보", "마감 넘기고 아무 연락도 없음"],
    level: "basic",
  },
  {
    id: "time_02",
    category: "time",
    categoryIcon: "ri-time-line",
    categoryColor: "#0D9488",
    title: "퇴근 전 오늘 처리 못한 일은 인수인계를",
    summary: "퇴근 전 5분 투자가 다음 날 아침의 신뢰를 만듭니다.",
    detail: "급하게 퇴근하면서 마무리 안 된 일을 방치하면, 다음 날 또는 다른 팀원이 당황하게 됩니다. 퇴근 전 5분 동안 오늘 못 마친 업무를 팀 채널이나 담당자에게 간단히 공유하는 것이 좋은 습관입니다.",
    doList: ["○○ 건은 내일 오전 중 마무리 예정입니다.", "A 파일은 50% 완성, 내일 오전 9시까지 완료 예정입니다."],
    dontList: ["말 없이 퇴근", "다음 날 '어제 못 했어요'로 시작", "담당자에게 알리지 않은 미완료 업무"],
    level: "basic",
  },

  // ─── 디지털 소통 ─────────────────────────────────────────────
  {
    id: "digital_01",
    category: "digital",
    categoryIcon: "ri-smartphone-line",
    categoryColor: "#6B9FF7",
    title: "메신저는 짧고 명확하게 — 줄 바꿈도 예절",
    summary: "메신저에서 긴 텍스트 덩어리는 읽기 어렵습니다.",
    detail: "업무 메신저(카카오워크, 슬랙, 라인웍스)에서 긴 내용을 한 문단으로 보내면 읽기 어렵습니다. 핵심을 먼저, 세부 내용은 줄 바꿈으로 구분하세요. '안녕하세요'만 먼저 보내고 기다리게 하는 것은 실례입니다.",
    doList: ["인사 + 용건을 한 번에 보내기", "항목이 여러 개면 1. 2. 3. 번호 매기기", "회신이 필요한 기한 명시"],
    dontList: ["안녕하세요 (전송) ...10분 후 용건 전송", "한 메시지에 500자 덩어리", "중요 업무를 메신저 이모티콘으로만 전달"],
    level: "basic",
  },
  {
    id: "digital_02",
    category: "digital",
    categoryIcon: "ri-smartphone-line",
    categoryColor: "#6B9FF7",
    title: "업무 전화는 3번 이내에 받거나, 콜백 필수",
    summary: "전화를 못 받으면 반드시 확인 후 10분 내 콜백하세요.",
    detail: "업무용 전화는 개인 전화와 달리 무시할 수 없습니다. 회의 중이거나 통화 불가 상황이라면, 문자로 '회의 중입니다. ○시 이후 연락드리겠습니다'라고 알리는 것이 예의입니다.",
    doList: ["전화 못 받으면 10분 내 콜백", "통화 불가 시 문자로 상황 공유", "중요 통화 내용은 메일로 요약·공유"],
    dontList: ["부재중 확인 후 콜백 없음", "업무 시간 전화 무시", "회의 중 전화 받으며 회의 방해"],
    level: "basic",
  },
  {
    id: "digital_03",
    category: "digital",
    categoryIcon: "ri-smartphone-line",
    categoryColor: "#6B9FF7",
    title: "밤 10시 이후 업무 메시지는 자제",
    summary: "야간 메시지는 상대방의 개인 시간을 침범합니다.",
    detail: "긴급한 경우가 아니라면, 밤 10시 이후에 업무 메시지를 보내는 것은 피하세요. 예약 발송 기능을 활용해 다음 날 아침 9시에 보내도록 설정하거나, 메모해 두었다가 다음 날 보내세요. 직장 내 괴롭힘 방지법에서도 야간 업무 연락을 규제합니다.",
    doList: ["예약 발송으로 다음 날 오전 9시 전송", "긴급 상황은 '긴급: 오늘 밤 처리 필요' 라고 명시", "비긴급 야간 메시지는 메모 후 다음 날 발송"],
    dontList: ["새벽 2시에 업무 지시 메시지", "주말 밤 긴급하지 않은 업무 요청", "야간 메시지 후 즉시 답장 기대"],
    level: "intermediate",
  },
];

// 오늘의 팁 (날짜 기반 고정)
export function getTodayTip(): EtiquetteTip {
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000);
  return ETIQUETTE_TIPS[dayOfYear % ETIQUETTE_TIPS.length];
}

// 상황별 팁 필터
export function getTipsByContext(situation: string): EtiquetteTip[] {
  return ETIQUETTE_TIPS.filter(t => t.situation === situation).slice(0, 3);
}

// 카테고리별 팁
export function getTipsByCategory(category: string): EtiquetteTip[] {
  return ETIQUETTE_TIPS.filter(t => t.category === category);
}
